/**
 * Class Schedule Create/Edit Form
 * Robust UX: inline validation, time-overlap detection, visual period list.
 * Req 19: Schedule period immutability when referenced by timetable records.
 */

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Chip,
  IconButton, Alert, Divider, Stack, Tooltip,
  ToggleButton, ToggleButtonGroup, Paper, Select,
  MenuItem, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Coffee as BreakIcon,
  AccessTime as PeriodIcon,
  ArrowDownward as MoveDownIcon,
  ArrowUpward as MoveUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createClassScheduleDirect, updateClassScheduleDirect, getSchedulePeriodUsageCount } from '@timetable/api';
import type { ClassScheduleWithPeriods, SchedulePeriod } from '@timetable/types';
import { queryKeys } from '../../lib/queryKeys';
import { ConfirmDialog } from '../ConfirmDialog';

const ALL_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
  THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun',
};

type PeriodRow = Omit<SchedulePeriod, 'id' | 'class_schedule_id' | 'created_at' | 'updated_at'> & {
  /** Original DB id — present only when editing an existing period */
  _id?: string
};

function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minsToTime(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const min = (m % 60).toString().padStart(2, '0');
  return `${h}:${min}`;
}

function defaultPeriod(num: number, prevEnd?: string, durationMins = 45): PeriodRow {
  const start = prevEnd ?? '08:00';
  const startMins = timeToMins(start);
  return {
    period_number: num,
    time_start: start,
    time_end: minsToTime(startMins + durationMins),
    is_break: false,
    break_type: null,
    period_name: null,
    requires_supervision: false,
    supervision_type: null,
  };
}

function defaultBreak(num: number, prevEnd?: string): PeriodRow {
  const start = prevEnd ?? '10:30';
  const startMins = timeToMins(start);
  return {
    period_number: num,
    time_start: start,
    time_end: minsToTime(startMins + 15),
    is_break: true,
    break_type: 'short',
    period_name: 'Break',
    requires_supervision: false,
    supervision_type: null,
  };
}

// ── Validation ────────────────────────────────────────────────────────────────

interface RowError {
  time_start?: string;
  time_end?: string;
  break_type?: string;
}

function validatePeriods(periods: PeriodRow[]): { rowErrors: RowError[]; globalError: string | null } {
  const rowErrors: RowError[] = periods.map(() => ({}));
  let globalError: string | null = null;

  periods.forEach((p, i) => {
    if (!p.time_start) rowErrors[i].time_start = 'Required';
    if (!p.time_end) rowErrors[i].time_end = 'Required';
    if (p.time_start && p.time_end && timeToMins(p.time_end) <= timeToMins(p.time_start)) {
      rowErrors[i].time_end = 'End must be after start';
    }
    if (p.is_break && !p.break_type) {
      rowErrors[i].break_type = 'Select break type';
    }
  });

  // Check overlaps
  const sorted = periods
    .map((p, i) => ({ ...p, idx: i }))
    .filter(p => p.time_start && p.time_end)
    .sort((a, b) => timeToMins(a.time_start) - timeToMins(b.time_start));

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (timeToMins(curr.time_start) < timeToMins(prev.time_end)) {
      rowErrors[curr.idx].time_start = `Overlaps with period ${prev.period_number}`;
      if (!globalError) globalError = 'Some periods overlap — fix the highlighted rows';
    }
  }

  return { rowErrors, globalError };
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  schoolId: string;
  schedule: ClassScheduleWithPeriods | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ClassScheduleForm({ open, onClose, schoolId, schedule }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!schedule;

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState('');
  const [workingDays, setWorkingDays] = useState<string[]>(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']);
  const [daysError, setDaysError] = useState('');
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  // Usage counts for existing period IDs (Req 19)
  const [usageCounts, setUsageCounts] = useState<Map<string, number>>(new Map());
  // Confirmation dialog for timing changes on referenced periods
  const [timingChangeConfirm, setTimingChangeConfirm] = useState<{ pendingFn: () => void; count: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setName(schedule.name);
      setDescription(schedule.description ?? '');
      setWorkingDays(schedule.working_days);
      const rows = [...schedule.schedule_periods]
        .sort((a, b) => a.period_number - b.period_number)
        .map(({ id, period_number, time_start, time_end, is_break, break_type, period_name, requires_supervision, supervision_type }, i) => ({
          _id: id,
          period_number: i + 1, // normalize to avoid legacy duplicates
          time_start, time_end, is_break,
          break_type: break_type ?? null,
          period_name: period_name ?? null,
          requires_supervision,
          supervision_type: supervision_type ?? null,
        }));
      setPeriods(rows);
      // Fetch usage counts for all existing periods
      const counts = new Map<string, number>();
      Promise.all(
        schedule.schedule_periods.map(p =>
          getSchedulePeriodUsageCount(p.id).then(count => counts.set(p.id, count)).catch(() => {})
        )
      ).then(() => setUsageCounts(new Map(counts)));
    } else {
      setName('');
      setDescription('');
      setWorkingDays(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']);
      setPeriods([]);
      setUsageCounts(new Map());
    }
    setNameError('');
    setDaysError('');
    setRowErrors([]);
    setGlobalError(null);
    setApiError(null);
  }, [open, schedule]);

  // Live validation on period changes
  useEffect(() => {
    const { rowErrors: re, globalError: ge } = validatePeriods(periods);
    setRowErrors(re);
    setGlobalError(ge);
  }, [periods]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        periods_count: periods.filter(p => !p.is_break).length,
        working_days: workingDays,
        // Strip internal _id field before sending to API
        schedule_periods: periods.map(({ _id: _ignored, ...p }) => p),
      };
      return isEdit
        ? updateClassScheduleDirect(schedule!.id, payload)
        : createClassScheduleDirect({ ...payload, school_id: schoolId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classSchedules.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedulePeriods.all, refetchType: 'all' });
      // Invalidate classSections so working_days from the joined class_schedule refreshes
      queryClient.invalidateQueries({ queryKey: queryKeys.classSections.all });
      onClose();
    },
    onError: (err: Error) => setApiError(err.message),
  });

  const handleSave = () => {
    let valid = true;
    if (!name.trim()) { setNameError('Schedule name is required'); valid = false; }
    if (workingDays.length === 0) { setDaysError('Select at least one working day'); valid = false; }
    if (periods.length === 0) { setGlobalError('Add at least one period'); valid = false; }
    const { rowErrors: re, globalError: ge } = validatePeriods(periods);
    setRowErrors(re);
    if (ge) { setGlobalError(ge); valid = false; }
    const hasRowError = re.some(e => Object.keys(e).length > 0);
    if (hasRowError) valid = false;
    if (valid) mutation.mutate();
  };

  const lastEnd = () => periods[periods.length - 1]?.time_end;

  // Smart inference: derive duration from the last teaching period, fall back to 45 min
  const inferDuration = () => {
    const lastTeaching = [...periods].reverse().find(p => !p.is_break);
    if (lastTeaching) {
      const d = timeToMins(lastTeaching.time_end) - timeToMins(lastTeaching.time_start);
      if (d > 0) return d;
    }
    return 45;
  };

  const addPeriod = () => {
    setPeriods(prev => [...prev, defaultPeriod(prev.length + 1, lastEnd(), inferDuration())]);
  };

  const addBreak = () => {
    setPeriods(prev => [...prev, defaultBreak(prev.length + 1, lastEnd())]);
  };

  const remove = (idx: number) => {
    const period = periods[idx];
    const periodId = period._id;
    const count = periodId ? (usageCounts.get(periodId) ?? 0) : 0;
    if (count > 0) {
      setApiError(`Cannot delete: this period slot is used by ${count} timetable record(s).`);
      return;
    }
    setPeriods(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((p, i) => ({ ...p, period_number: i + 1 }));
    });
  };

  const move = (idx: number, dir: -1 | 1) => {
    setPeriods(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];

      // Recascade times: keep each item's duration, chain start = prev end
      const firstStart = timeToMins(next[0].time_start);
      let cursor = firstStart;
      return next.map((p, i) => {
        const duration = timeToMins(p.time_end) - timeToMins(p.time_start);
        const newStart = cursor;
        const newEnd = cursor + Math.max(duration, 1);
        cursor = newEnd;
        return { ...p, period_number: i + 1, time_start: minsToTime(newStart), time_end: minsToTime(newEnd) };
      });
    });
  };

  const TIMING_FIELDS: (keyof PeriodRow)[] = ['time_start', 'time_end', 'period_number'];

  const update = (idx: number, field: keyof PeriodRow, value: any) => {
    const period = periods[idx];
    const periodId = period._id;
    const count = periodId ? (usageCounts.get(periodId) ?? 0) : 0;

    const doUpdate = () => {
      setPeriods(prev => prev.map((p, i) => {
        if (i !== idx) return p;
        const u = { ...p, [field]: value };
        if (field === 'is_break' && !value) {
          u.break_type = null; u.requires_supervision = false; u.supervision_type = null;
        }
        if (field === 'requires_supervision' && !value) u.supervision_type = null;
        return u;
      }));
    };

    if (count > 0 && TIMING_FIELDS.includes(field)) {
      setTimingChangeConfirm({ pendingFn: doUpdate, count });
    } else {
      doUpdate();
    }
  };

  const teachCount = periods.filter(p => !p.is_break).length;
  const breakCount = periods.filter(p => p.is_break).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {isEdit ? `Edit: ${schedule!.name}` : 'New Class Schedule'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Define the daily period structure that will be applied to classes
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {(apiError || globalError) && (
          <Alert
            severity="error"
            onClose={() => { setApiError(null); setGlobalError(null); }}
            sx={{ mx: 3, mt: 2 }}
          >
            {apiError ?? globalError}
          </Alert>
        )}

        {/* ── Section 1: Basic Info ── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Schedule Info
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Schedule Name"
              value={name}
              onChange={e => { setName(e.target.value); if (e.target.value.trim()) setNameError(''); }}
              error={!!nameError}
              helperText={nameError || 'e.g. "Primary Schedule", "Secondary Shift A"'}
              required
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              size="small"
              helperText="Short note for admins"
            />
          </Stack>
        </Box>

        <Divider />

        {/* ── Section 2: Working Days ── */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Working Days
          </Typography>
          <Box sx={{ mt: 1 }}>
            <ToggleButtonGroup
              value={workingDays}
              onChange={(_, val) => {
                setWorkingDays(val);
                setDaysError(val.length === 0 ? 'Select at least one day' : '');
              }}
              size="small"
              sx={{ flexWrap: 'wrap', gap: 0.5 }}
            >
              {ALL_DAYS.map(day => (
                <ToggleButton
                  key={day}
                  value={day}
                  sx={{
                    px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600,
                    borderRadius: '20px !important',
                    border: '1px solid !important',
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main !important' },
                  }}
                >
                  {DAY_LABELS[day]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {daysError && <FormHelperText error sx={{ mt: 0.5 }}>{daysError}</FormHelperText>}
          </Box>
        </Box>

        <Divider />

        {/* ── Section 3: Periods ── */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Periods & Breaks
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip icon={<PeriodIcon sx={{ fontSize: '14px !important' }} />} label={`${teachCount} teaching`} size="small" color="primary" variant="outlined" />
                {breakCount > 0 && <Chip icon={<BreakIcon sx={{ fontSize: '14px !important' }} />} label={`${breakCount} break${breakCount > 1 ? 's' : ''}`} size="small" color="warning" variant="outlined" />}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<BreakIcon />} onClick={addBreak} color="warning">
                Add Break
              </Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addPeriod}>
                Add Period
              </Button>
            </Stack>
          </Box>

          {periods.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                No periods yet. Click "Add Period" to start building the schedule.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1} sx={{ pb: 1 }}>
              {periods.map((period, idx) => (
                <PeriodRowItem
                  key={idx}
                  period={period}
                  idx={idx}
                  total={periods.length}
                  errors={rowErrors[idx] ?? {}}
                  usageCount={period._id ? (usageCounts.get(period._id) ?? 0) : 0}
                  onUpdate={(field, val) => update(idx, field, val)}
                  onRemove={() => remove(idx)}
                  onMoveUp={() => move(idx, -1)}
                  onMoveDown={() => move(idx, 1)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={mutation.isPending} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={mutation.isPending}
          sx={{ minWidth: 130 }}
        >
          {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Schedule'}
        </Button>
      </DialogActions>

      {/* Timing change confirmation for referenced periods (Req 19.2) */}
      <ConfirmDialog
        open={!!timingChangeConfirm}
        title="Change Referenced Period Timing"
        message={
          timingChangeConfirm
            ? `This period slot is used by ${timingChangeConfirm.count} timetable record(s). Changing its timing may affect existing assignments. Proceed?`
            : ''
        }
        onConfirm={() => {
          timingChangeConfirm?.pendingFn();
          setTimingChangeConfirm(null);
        }}
        onCancel={() => setTimingChangeConfirm(null)}
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        severity="warning"
      />
    </Dialog>
  );
}

// ── Period Row Item ────────────────────────────────────────────────────────────────

interface PeriodRowItemProps {
  period: PeriodRow;
  idx: number;
  total: number;
  errors: RowError;
  usageCount: number;
  onUpdate: (field: keyof PeriodRow, value: any) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function PeriodRowItem({ period, idx, total, errors, usageCount, onUpdate, onRemove, onMoveUp, onMoveDown }: PeriodRowItemProps) {
  const isBreak = period.is_break;
  const hasError = Object.keys(errors).length > 0;
  const isReferenced = usageCount > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: hasError ? 'error.main' : isReferenced ? 'info.light' : isBreak ? 'warning.light' : 'divider',
        bgcolor: isBreak ? 'warning.50' : 'background.paper',
        borderRadius: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Drag handle / order */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1, mb: 0.25 }}>
            {idx + 1}
          </Typography>
          <DragIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Box>

        {/* Type badge + referenced indicator */}
        <Box sx={{ pt: 0.75 }}>
          <Chip
            label={isBreak ? 'Break' : 'Period'}
            size="small"
            color={isBreak ? 'warning' : 'primary'}
            variant="filled"
            sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer' }}
            onClick={() => onUpdate('is_break', !isBreak)}
          />
          {isReferenced && (
            <Tooltip title={`Used by ${usageCount} timetable record(s). Timing changes require confirmation.`}>
              <Chip
                icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
                label={`${usageCount} records`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 18, mt: 0.25 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Fields */}
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
          {/* Name */}
          <TextField
            label={isBreak ? 'Break Name' : 'Period Name'}
            value={period.period_name ?? ''}
            onChange={e => onUpdate('period_name', e.target.value || null)}
            size="small"
            placeholder={isBreak ? 'e.g. Lunch' : `Period ${period.period_number}`}
            sx={{ width: 140 }}
          />

          {/* Start time */}
          <TextField
            label="Start"
            type="time"
            value={period.time_start}
            onChange={e => onUpdate('time_start', e.target.value)}
            size="small"
            sx={{ width: 140 }}
            error={!!errors.time_start}
            helperText={errors.time_start}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />

          {/* End time */}
          <TextField
            label="End"
            type="time"
            value={period.time_end}
            onChange={e => onUpdate('time_end', e.target.value)}
            size="small"
            sx={{ width: 140 }}
            error={!!errors.time_end}
            helperText={errors.time_end}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />

          {/* Duration badge */}
          {period.time_start && period.time_end && timeToMins(period.time_end) > timeToMins(period.time_start) && (
            <Box sx={{ pt: 1 }}>
              <Chip
                label={`${timeToMins(period.time_end) - timeToMins(period.time_start)} min`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>
          )}

          {/* Break type (only for breaks) */}
          {isBreak && (
            <FormControl size="small" sx={{ width: 130 }} error={!!errors.break_type}>
              <InputLabel>Break Type *</InputLabel>
              <Select
                value={period.break_type ?? ''}
                label="Break Type *"
                onChange={e => onUpdate('break_type', e.target.value || null)}
              >
                <MenuItem value="short">Short Break</MenuItem>
                <MenuItem value="recess">Recess</MenuItem>
                <MenuItem value="lunch">Lunch Break</MenuItem>
              </Select>
              {errors.break_type && <FormHelperText>{errors.break_type}</FormHelperText>}
            </FormControl>
          )}
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={idx === 0}>
                <MoveUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={idx === total - 1}>
                <MoveDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={onRemove}
                disabled={isReferenced}
                title={isReferenced ? `Used by ${usageCount} timetable record(s) — cannot delete` : 'Remove'}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
}
